import { z } from 'zod';
import { RelationshipType } from './network.js';

export const MemberStatus = z.enum(['NEW', 'ACTIVE', 'ARCHIVED']);
export type MemberStatus = z.infer<typeof MemberStatus>;

export const MemberDTO = z.object({
  id: z.string(),
  name: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  company: z.string().nullable(),
  role: z.string().nullable(),
  industry: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  linkedin: z.string().nullable(),
  instagram: z.string().nullable(),
  xing: z.string().nullable(),
  /** Straße + Hausnummer — zusammen mit PLZ Basis für exakte Kartenpunkte. */
  street: z.string().nullable(),
  zip: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  /** ISO-Datum (YYYY-MM-DD) oder null. */
  birthday: z.string().nullable(),
  tags: z.array(z.string()),
  notes: z.string().nullable(),
  status: MemberStatus,
  photoUrl: z.string().nullable(),
  isSelf: z.boolean(),
  pitch: z.string().nullable(),
  offering: z.string().nullable(),
  seeking: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Werte der eigenen Felder des Besitzers — Schlüssel siehe /members/fields. */
  customFields: z.record(z.union([z.string(), z.number(), z.boolean()])).default({}),
  /** Gesetzt, wenn die Vernetzung über eine Netzwerk-Gruppe kam ("Vernetzt durch Gruppe X"). */
  connectedViaGroup: z.object({ id: z.string(), name: z.string() }).nullable().default(null),
});
export type MemberDTO = z.infer<typeof MemberDTO>;

// 5000 statt 2000: der Import erlaubt schon immer 5000 Zeichen Notizen — mit einem
// strengeren Edit-Limit wären importierte Kontakte beim ersten Speichern unrettbar.
const optStr = z.string().trim().max(5000).optional().nullable();
const optShort = z.string().trim().max(200).optional().nullable();
const optPhoto = z.string().trim().max(2_000_000).optional().nullable();

export const MemberCreateInput = z.object({
  firstName: z.string().trim().min(1).max(80),
  // Optional: CSV-Importe und einteilige Namen ("Madonna GmbH-Kontakt") haben keinen
  // Nachnamen — ein Pflichtfeld würde diese Kontakte dauerhaft unspeicherbar machen.
  lastName: z
    .string()
    .trim()
    .max(80)
    .optional()
    .nullable()
    .or(z.literal('').transform(() => null)),
  company: optShort,
  role: optShort,
  industry: optShort,
  email: z.string().trim().email().optional().nullable().or(z.literal('').transform(() => null)),
  phone: optShort,
  website: optShort,
  linkedin: optShort,
  instagram: optShort,
  xing: optShort,
  street: optShort,
  zip: optShort,
  city: optShort,
  country: optShort,
  lat: z.number().optional().nullable(),
  lng: z.number().optional().nullable(),
  // YYYY-MM-DD; leerer String kommt aus dem Formular und bedeutet "nicht gesetzt".
  birthday: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum im Format YYYY-MM-DD erwartet')
    .optional()
    .nullable()
    .or(z.literal('').transform(() => null)),
  tags: z.array(z.string().trim().min(1).max(60)).max(50).optional(),
  notes: optStr,
  status: MemberStatus.optional(),
  photoUrl: optPhoto,
  pitch: optStr,
  offering: optStr,
  seeking: optStr,
  /**
   * Werte der eigenen Felder: { "<key>": Wert }. Unbekannte Schlüssel verwirft
   * der Server — so kann ein altes Formular keine Geisterfelder anlegen.
   */
  customFields: z.record(z.union([z.string().max(5000), z.number(), z.boolean(), z.null()])).optional(),
});
export type MemberCreateInput = z.infer<typeof MemberCreateInput>;

export const MemberUpdateInput = MemberCreateInput.partial();
export type MemberUpdateInput = z.infer<typeof MemberUpdateInput>;

export const MemberListQuery = z.object({
  search: z.string().trim().min(1).max(200).optional(),
  tag: z.string().trim().min(1).max(60).optional(),
  status: MemberStatus.optional(),
  /** Eigenes Profil (isSelf) mit ausliefern — z.B. für die Match- und Deal-Auswahl. */
  includeSelf: z.coerce.boolean().optional(),
  /** Nur Mitglieder mit Geburtstag in den nächsten N Tagen. */
  birthdayWithinDays: z.coerce.number().int().min(1).max(365).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type MemberListQuery = z.infer<typeof MemberListQuery>;

export const MemberListResponse = z.object({
  members: z.array(MemberDTO),
  total: z.number(),
});
export type MemberListResponse = z.infer<typeof MemberListResponse>;

export const MemberResponse = z.object({ member: MemberDTO });
export type MemberResponse = z.infer<typeof MemberResponse>;

// --- Sprachnotiz auswerten ---
//
// Der Nutzer erzählt frei, was er über die Person weiß. Der Browser macht daraus Text
// (Web Speech API), Claude sortiert den Text in Felder — Claude selbst kann kein Audio.

export const MemberExtractInput = z.object({
  transcript: z.string().trim().min(3).max(8000),
  /** Was schon bekannt ist (aus vCard/Formular) — die KI soll es nicht überschreiben, nur ergänzen. */
  known: z
    .object({
      name: z.string().optional(),
      company: z.string().nullable().optional(),
      role: z.string().nullable().optional(),
      city: z.string().nullable().optional(),
    })
    .optional(),
});
export type MemberExtractInput = z.infer<typeof MemberExtractInput>;

/** Eine erkannte Verbindung zu einem Kontakt, den es schon im Netzwerk gibt. */
export const ExtractedLink = z.object({
  memberId: z.string(),
  memberName: z.string(),
  type: RelationshipType,
  /** Warum die KI diese Verbindung sieht — wörtlich aus dem Gesagten belegt. */
  reason: z.string(),
});
export type ExtractedLink = z.infer<typeof ExtractedLink>;

export const ExtractedMember = z.object({
  company: z.string().nullable(),
  role: z.string().nullable(),
  industry: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  offering: z.string().nullable(),
  seeking: z.string().nullable(),
  tags: z.array(z.string()),
  notes: z.string().nullable(),
  /**
   * Die tatsächlich belegten Felder — wird serverseitig aus den non-null-Werten
   * abgeleitet, nicht aus der (unzuverlässigen) Selbstauskunft des Modells.
   */
  filled: z.array(z.string()),
  links: z.array(ExtractedLink),
});
export type ExtractedMember = z.infer<typeof ExtractedMember>;

export const MemberExtractResponse = z.object({
  extracted: ExtractedMember,
});
export type MemberExtractResponse = z.infer<typeof MemberExtractResponse>;

// --- Kontakte einpflegen (Swipe-Stapel) ---

/** Fingerabdrücke bereits weggeswipter Kontakte — die UI blendet sie aus dem Stapel aus. */
export const DismissedListResponse = z.object({
  fingerprints: z.array(z.string()),
});
export type DismissedListResponse = z.infer<typeof DismissedListResponse>;

export const DismissContactsInput = z.object({
  fingerprints: z.array(z.string().min(1).max(300)).min(1).max(2000),
});
export type DismissContactsInput = z.infer<typeof DismissContactsInput>;

export const MemberImportRow = z.object({
  name: z.string().trim().min(1).max(200),
  company: z.string().trim().max(200).optional(),
  role: z.string().trim().max(200).optional(),
  industry: z.string().trim().max(200).optional(),
  email: z.string().trim().max(200).optional(),
  phone: z.string().trim().max(200).optional(),
  website: z.string().trim().max(200).optional(),
  linkedin: z.string().trim().max(200).optional(),
  street: z.string().trim().max(200).optional(),
  zip: z.string().trim().max(30).optional(),
  city: z.string().trim().max(200).optional(),
  country: z.string().trim().max(200).optional(),
  notes: z.string().trim().max(5000).optional(),
  tags: z.array(z.string().trim().min(1).max(60)).optional(),
});
export type MemberImportRow = z.infer<typeof MemberImportRow>;

export const MemberImportInput = z.object({
  rows: z.array(MemberImportRow).min(1).max(2000),
});
export type MemberImportInput = z.infer<typeof MemberImportInput>;

export const MemberImportResult = z.object({
  created: z.number(),
  skipped: z.number(),
  errors: z.array(z.object({ index: z.number(), error: z.string() })),
});
export type MemberImportResult = z.infer<typeof MemberImportResult>;

// --- Import-Assistent (KI) ---

/**
 * Fremde CSV-Struktur: Kopfzeile + Beispielzeilen → Spalten-Zuordnung.
 *
 * Die Längenlimits sind absichtlich großzügig und schneiden ab, statt die
 * Anfrage abzulehnen: Eine einzige lange Notiz-Zelle im CRM-Export ließ sonst
 * die komplette KI-Zuordnung mit "invalid_input" auflaufen. Für die Zuordnung
 * reicht ein Anriss der Zelle — sie soll nur zeigen, wie die Spalte aussieht.
 */
export const ImportMapInput = z.object({
  header: z.array(z.string().max(2000).transform((s) => s.slice(0, 200))).min(1).max(200),
  /** Ein paar Beispielzeilen geben der KI Kontext ("sieht aus wie Telefonnummern"). */
  sample: z
    .array(z.array(z.string().max(20000).transform((s) => s.slice(0, 200))).max(200))
    .max(10),
});
export type ImportMapInput = z.infer<typeof ImportMapInput>;

export const ImportMapResponse = z.object({
  /** Pro Spalte der Ziel-Feldname oder null (= ignorieren). */
  mapping: z.array(z.string().nullable()),
});
export type ImportMapResponse = z.infer<typeof ImportMapResponse>;

/** Unstrukturierter Text (Mail-Signaturen, Messenger-Verlauf, Notizen) → Kontakt-Zeilen. */
export const ImportFreetextInput = z.object({
  text: z.string().trim().min(3).max(20000),
});
export type ImportFreetextInput = z.infer<typeof ImportFreetextInput>;

export const ImportFreetextResponse = z.object({
  rows: z.array(MemberImportRow),
});
export type ImportFreetextResponse = z.infer<typeof ImportFreetextResponse>;

export const TagListResponse = z.object({ tags: z.array(z.string()) });
export type TagListResponse = z.infer<typeof TagListResponse>;

export const ProfileResponse = z.object({ member: MemberDTO.nullable() });
export type ProfileResponse = z.infer<typeof ProfileResponse>;

// --- Eigene Felder (pro Nutzer) ---

export const MemberFieldType = z.enum([
  'TEXT',
  'LONGTEXT',
  'NUMBER',
  'DATE',
  'SELECT',
  'CHECKBOX',
]);
export type MemberFieldType = z.infer<typeof MemberFieldType>;

export const MemberFieldDefDTO = z.object({
  id: z.string(),
  key: z.string(),
  label: z.string(),
  type: MemberFieldType,
  options: z.array(z.string()),
  position: z.number(),
});
export type MemberFieldDefDTO = z.infer<typeof MemberFieldDefDTO>;

export const MemberFieldCreateInput = z.object({
  label: z.string().trim().min(1).max(60),
  type: MemberFieldType.optional(),
  options: z.array(z.string().trim().min(1).max(60)).max(50).optional(),
});
export type MemberFieldCreateInput = z.infer<typeof MemberFieldCreateInput>;

export const MemberFieldUpdateInput = z.object({
  label: z.string().trim().min(1).max(60).optional(),
  options: z.array(z.string().trim().min(1).max(60)).max(50).optional(),
  position: z.number().int().min(0).max(999).optional(),
});
export type MemberFieldUpdateInput = z.infer<typeof MemberFieldUpdateInput>;

export const MemberFieldListResponse = z.object({ fields: z.array(MemberFieldDefDTO) });
export type MemberFieldListResponse = z.infer<typeof MemberFieldListResponse>;

export const MemberFieldResponse = z.object({ field: MemberFieldDefDTO });
export type MemberFieldResponse = z.infer<typeof MemberFieldResponse>;
